import type { Address } from '../../../stores/useAppointmentsStore';
import type {
  InvoiceDetail,
  InvoiceDetailLinks,
  InvoiceLineItem,
  InvoiceNote,
  InvoiceSignature,
  ProgressBilling,
  Transaction,
} from '../interfaces/InvoiceService';

/**
 * Adapter for the rich invoice-detail wire returned by `GET /v2/invoices/:id`.
 *
 * Wire-contract sources (read 2026-06-04, this session):
 *   - ../api/app/views/v2/invoices/_invoice.json.jbuilder        (base payload)
 *   - ../api/app/views/v2/invoices/_progress_billing.json.jbuilder (nested SPB)
 *   - ../api/app/views/v2/transactions/_transaction.json.jbuilder  (per-type txn)
 *   - ../api/app/views/v1/comments/_comment.json.jbuilder          (notes)
 *   - ../api/app/views/v2/signatures/_signature.json.jbuilder      (signature)
 *
 * Field-by-field map (jbuilder field → domain field):
 *
 *   | jbuilder field                       | adapter behaviour                                  |
 *   | ------------------------------------ | -------------------------------------------------- |
 *   | invoice_id                           | id (normalizeId — tolerates stringy ids)           |
 *   | job_id (present only when job)       | jobId (omit when absent)                           |
 *   | invoice_date (iso8601, nullable)     | invoiceDate (omit-vs-preserve)                     |
 *   | cancelled / voided / finalized       | booleans (default false)                           |
 *   | format (display_format)              | displayFormat (omit-vs-preserve)                   |
 *   | display_membership_savings           | displayMembershipSavings (boolean passthrough)     |
 *   | customer.customer_short              | customer{customerId,fullName,email,phone}          |
 *   | billing_address / service_address    | adaptAddress (street→address1, unit→address2)      |
 *   | items_attributes[] (flat — D-05)     | lineItems[] (cents via safeCents)                  |
 *   | financials.*_cents                   | safeCents → 0 default (money is integer cents)     |
 *   | financials.savings_cents             | savingsCents — omit when absent, preserve incl. 0  |
 *   | payments[]                           | payments: Transaction[] (per-type flat fields)     |
 *   | signature{image_url,signature_time}  | signature{signatureUrl,signedAt} (omit-vs-preserve)|
 *   | notes[] (comment/pinned/created_at)  | notes[] (HTML verbatim; author from user.full_name)|
 *   | links.invoice_pdf / pdf_url          | pdfUrl (prefer links.invoice_pdf; D-16)            |
 *   | progress_billing (NESTED, nullable)  | progressBilling (SPB guard; D-01/D-03/D-09)        |
 *
 * Money on this payload is already integer `*_cents` (unlike the 5.1 list wire's
 * dollar strings) — this adapter does NOT do any dollar-string parsing; all
 * money is read directly as cents via `safeCents`.
 * Optional fields are OMITTED (key absent) when the wire ships null/undefined so
 * consumers can use `'field' in invoice`. HTML strings (notes/descriptions) are
 * carried verbatim; the view decodes/sanitizes.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Wire interface — snake_case mirror of the jbuilder, nullable + index-open.
// ─────────────────────────────────────────────────────────────────────────────

export interface InvoiceDetailWireAddress {
  id?: number;
  street?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  time_zone?: string | null;
  primary?: boolean;
  name?: string | null;
  invalid?: boolean;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireCustomer {
  customer_id?: number;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireLineItem {
  id?: number | string;
  item_type?: string | null;
  unit_name?: string | null;
  quantity?: number | string | null;
  unit_price_cents?: number | string | null;
  total_cents?: number | string | null;
  description?: string | null;
  warranty?: string | null;
  covered_systems?: string | null;
  // Grouping (05.6 — `?item_grouping=1`). The `[extra]` index already admits
  // these; the explicit keys are for type-safety/docs. Group nodes carry their
  // aggregate price in the SAME unit_price_cents/total_cents keys. The deprecated
  // group-total mirror key is intentionally NOT typed/read (Pitfall 1) — use
  // the aggregate total_cents above instead.
  type?: 'group' | 'lineItem' | null;
  group_id?: number | null;
  items_in_group?: InvoiceDetailWireLineItem[] | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireFinancials {
  subtotal_cents?: number | string | null;
  discount_amount_cents?: number | string | null;
  late_fee_cents?: number | string | null;
  sales_tax_cents?: number | string | null;
  total_cents?: number | string | null;
  total_transactions_cents?: number | string | null;
  balance_cents?: number | string | null;
  savings_cents?: number | string | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireTransaction {
  id?: number | string;
  transaction_time?: string | null;
  transaction_type?: string | null;
  amount_cents?: number | string | null;
  status?: string | null;
  card_last4?: string | null;
  card_brand?: string | null;
  entry_method?: string | null;
  ach_last4?: string | null;
  ach_account_type?: string | null;
  check_number?: string | number | null;
  processor_name?: string | null;
  notes?: string | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireNote {
  comment?: string | null;
  pinned?: boolean;
  created_at?: string | null;
  user?: { full_name?: string | null; [extra: string]: unknown } | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireSignature {
  kind?: string | null;
  image_url?: string | null;
  signature_time?: string | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireCurrentDraw {
  label?: string | null;
  amount_cents?: number | string | null;
  remaining_cents?: number | string | null;
  due_date?: string | null;
  is_due?: boolean;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireInstallment {
  id?: number;
  position?: number;
  label?: string | null;
  amount_cents?: number | string | null;
  paid_cents?: number | string | null;
  remaining_cents?: number | string | null;
  percentage?: number;
  due_date?: string | null;
  status?: string | null;
  collectible?: boolean;
  [extra: string]: unknown;
}

export interface InvoiceDetailWireProgressBilling {
  is_progress_billing?: boolean;
  cancelled?: boolean;
  payment_status?: string;
  past_due_amount_cents?: number | string | null;
  contract_total_cents?: number | string | null;
  paid_to_date_cents?: number | string | null;
  total_balance_remaining_cents?: number | string | null;
  current_draw?: InvoiceDetailWireCurrentDraw | null;
  next_draw_after_current?: {
    label?: string | null;
    due_date?: string | null;
  } | null;
  installments?: InvoiceDetailWireInstallment[] | null;
  [extra: string]: unknown;
}

export interface InvoiceDetailWire {
  invoice_id: number | string;
  job_id?: number | null;
  invoice_date?: string | null;
  cancelled?: boolean;
  voided?: boolean;
  finalized?: boolean;
  format?: string | null;
  display_membership_savings?: boolean;
  customer?: InvoiceDetailWireCustomer | null;
  billing_address?: InvoiceDetailWireAddress | null;
  service_address?: InvoiceDetailWireAddress | null;
  items_attributes?: InvoiceDetailWireLineItem[] | null;
  financials?: InvoiceDetailWireFinancials | null;
  payments?: InvoiceDetailWireTransaction[] | null;
  signature?: InvoiceDetailWireSignature | null;
  notes?: InvoiceDetailWireNote[] | null;
  links?: InvoiceDetailLinks | null;
  pdf_url?: string | null;
  // `.../v2/invoices/:id/transactions?next_payment_key=<id:total:balance[:failed]>`.
  // The server's authoritative idempotency key is embedded here as a query param
  // (the only place the invoice payload exposes it — there is no top-level field).
  payment_create_url?: string | null;
  progress_billing?: InvoiceDetailWireProgressBilling | null;
  [extra: string]: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper idioms — mirror quoteDetail.ts / invoiceRow.ts.
// ─────────────────────────────────────────────────────────────────────────────

/** Coerce a wire id (number or string-numeric) to a number. Returns 0 on NaN. */
function normalizeId(raw: number | string | undefined | null): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (raw === undefined || raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Defensive numeric coerce: NaN/Infinity/non-numeric/undefined → 0. */
function safeNumber(raw: number | string | null | undefined): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (raw === null || raw === undefined) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Money-specific alias of `safeNumber` (IN-03). Use `safeCents` at integer-cents
 * call sites so the "cents" semantics are self-documenting, and `safeNumber`
 * for non-money numerics like `quantity` — same coercion, clearer intent.
 */
const safeCents = safeNumber;

function str(value: string | null | undefined, fallback = ''): string {
  return value ?? fallback;
}

const EMPTY_ADDRESS: Address = {
  id: 0,
  address1: '',
  city: '',
  state: '',
  zip_code: '',
  time_zone: '',
};

/** Map the v2 address wire to the shared `Address` shape (street→address1). */
function adaptAddress(
  wire: InvoiceDetailWireAddress | null | undefined,
): Address {
  if (!wire) return { ...EMPTY_ADDRESS };
  const unit = (wire.unit ?? '').trim();
  const base: Address = {
    id: wire.id ?? 0,
    address1: str(wire.street),
    city: str(wire.city),
    state: str(wire.state),
    zip_code: str(wire.zip_code),
    time_zone: str(wire.time_zone),
  };
  if (wire.name != null) base.name = wire.name;
  if (typeof wire.invalid === 'boolean') base.invalid = wire.invalid;
  if (typeof wire.primary === 'boolean') base.primary = wire.primary;
  return unit ? { ...base, address2: unit } : base;
}

function adaptCustomer(
  wire: InvoiceDetailWireCustomer | null | undefined,
): InvoiceDetail['customer'] | undefined {
  if (!wire) return undefined;
  const out: NonNullable<InvoiceDetail['customer']> = {
    customerId: wire.customer_id ?? 0,
  };
  if (wire.full_name != null) out.fullName = wire.full_name;
  if (wire.email != null) out.email = wire.email;
  if (wire.phone != null) out.phone = wire.phone;
  return out;
}

/** Per-item adapter — money via safeCents; optional keys omit-vs-preserve. */
function adaptLineItem(wire: InvoiceDetailWireLineItem): InvoiceLineItem {
  const item: InvoiceLineItem = {
    id: normalizeId(wire.id),
    quantity: safeNumber(wire.quantity),
    unitPriceCents: safeCents(wire.unit_price_cents),
    totalCents: safeCents(wire.total_cents),
  };
  if (wire.unit_name != null) item.unitName = wire.unit_name;
  if (wire.description != null) item.description = wire.description;
  if (wire.warranty != null) item.warranty = wire.warranty;
  if (wire.item_type != null) item.itemType = wire.item_type;
  if (wire.covered_systems != null) item.coveredSystems = wire.covered_systems;
  return item;
}

/**
 * Group-node adapter (05.6, D-G4). A `type:'group'` wire node maps to a leader
 * `InvoiceLineItem` carrying the group's AGGREGATE prices (`unit_price_cents` /
 * `total_cents` — never the deprecated group-total mirror, Pitfall 1) plus
 * `itemsInGroup`, the children mapped through the UNTOUCHED `adaptLineItem`
 * (children carry no price keys for customers → `safeCents` 0-coerces them).
 *
 * The group node has no `unit_name`/`quantity`, so the leader's title-driving
 * fields + quantity derive from the FIRST child (omit-vs-preserve copy), letting
 * the existing render helpers (`itemTitle`/`showDescription`) work unchanged.
 * An empty/absent `items_in_group` → `children[0]` undefined → qty 0, no crash.
 */
function adaptGroupNode(wire: InvoiceDetailWireLineItem): InvoiceLineItem {
  const children = (wire.items_in_group ?? []).map(adaptLineItem);
  const leader: InvoiceLineItem = {
    id: normalizeId(wire.group_id),
    quantity: safeNumber(children[0]?.quantity),
    unitPriceCents: safeCents(wire.unit_price_cents),
    totalCents: safeCents(wire.total_cents),
    type: 'group',
    groupId: wire.group_id ?? null,
    itemsInGroup: children,
  };
  const first = children[0];
  if (first) {
    if (first.unitName != null) leader.unitName = first.unitName;
    if (first.description != null) leader.description = first.description;
    if (first.warranty != null) leader.warranty = first.warranty;
    if (first.itemType != null) leader.itemType = first.itemType;
    if (first.coveredSystems != null)
      leader.coveredSystems = first.coveredSystems;
  }
  return leader;
}

/**
 * Dispatcher over a mixed `items_attributes` node (05.6). Branches a group node
 * to `adaptGroupNode`; everything else (flat `type:'lineItem'` or a 5.2 payload
 * with no `type` key) falls through to the untouched flat `adaptLineItem` —
 * backward-compat with 5.2 is structurally guaranteed (D-G2).
 */
function adaptLineItemNode(wire: InvoiceDetailWireLineItem): InvoiceLineItem {
  return wire.type === 'group' ? adaptGroupNode(wire) : adaptLineItem(wire);
}

/**
 * Per-transaction adapter. The wire emits per-type fields FLAT, gated by
 * `transaction_type` (card_last4, ach_last4, check_number, processor_name).
 * Negative amount_cents is preserved verbatim (refund — D-13). Optional keys
 * are omitted when the type didn't ship them.
 */
function adaptTransaction(wire: InvoiceDetailWireTransaction): Transaction {
  const txn: Transaction = {
    id: normalizeId(wire.id),
    transactionTime: str(wire.transaction_time),
    transactionType: str(wire.transaction_type, 'cash'),
    amountCents: safeCents(wire.amount_cents),
    status: str(wire.status),
  };
  if (wire.card_last4 != null) txn.cardLast4 = wire.card_last4;
  if (wire.card_brand != null) txn.cardBrand = wire.card_brand;
  if (wire.entry_method != null) txn.entryMethod = wire.entry_method;
  if (wire.ach_last4 != null) txn.achLast4 = wire.ach_last4;
  if (wire.ach_account_type != null) txn.achAccountType = wire.ach_account_type;
  if (wire.check_number != null) txn.checkNumber = String(wire.check_number);
  if (wire.processor_name != null) txn.processorName = wire.processor_name;
  if (wire.notes != null) txn.notes = wire.notes;
  return txn;
}

/** Note adapter — HTML comment verbatim; author from user.full_name. */
function adaptNote(wire: InvoiceDetailWireNote): InvoiceNote {
  const note: InvoiceNote = {
    createdAt: str(wire.created_at),
    comment: str(wire.comment),
    pinned: wire.pinned === true,
  };
  const author = wire.user?.full_name;
  if (author != null) note.author = author;
  return note;
}

/** Signature adapter — image_url → signatureUrl; omit-vs-preserve signedAt. */
function adaptSignature(
  wire: InvoiceDetailWireSignature | null | undefined,
): InvoiceSignature | undefined {
  if (!wire || wire.image_url == null) return undefined;
  const out: InvoiceSignature = { signatureUrl: wire.image_url };
  if (wire.signature_time != null) out.signedAt = wire.signature_time;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress billing — SPB guards re-implemented against the NESTED detail wire.
// invoiceRow.ts is the model (D-01: that file is NOT edited; guards re-derived).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Closed set of SPB payment-status codes the domain understands, mirroring
 * `ProgressBilling::InvoicePresenter::PAYMENT_STATUSES`. An out-of-taxonomy code
 * falls through to non-SPB (WR-01) so a card never mislabels an unknown status.
 */
const KNOWN_PAYMENT_STATUSES: ReadonlySet<ProgressBilling['paymentStatus']> =
  new Set<ProgressBilling['paymentStatus']>([
    'voided',
    'current_draw_due',
    'past_due',
    'no_payment_due',
    'paid_in_full',
  ]);

function isKnownPaymentStatus(
  code: unknown,
): code is ProgressBilling['paymentStatus'] {
  return (
    typeof code === 'string' &&
    KNOWN_PAYMENT_STATUSES.has(code as ProgressBilling['paymentStatus'])
  );
}

function buildCurrentDraw(
  wire: InvoiceDetailWireCurrentDraw | null | undefined,
): ProgressBilling['currentDraw'] | undefined {
  if (!wire || wire.label == null) return undefined;
  return {
    label: wire.label,
    amountCents: safeCents(wire.amount_cents),
    remainingCents: safeCents(wire.remaining_cents),
    dueDate: wire.due_date ?? '',
    isDue: wire.is_due ?? false,
  };
}

function adaptInstallment(
  wire: InvoiceDetailWireInstallment,
): NonNullable<ProgressBilling['installments']>[number] {
  const inst: NonNullable<ProgressBilling['installments']>[number] = {
    position: wire.position ?? 0,
    label: str(wire.label),
    amountCents: safeCents(wire.amount_cents),
    paidCents: safeCents(wire.paid_cents),
    status: str(wire.status),
    dueDate: wire.due_date ?? '',
  };
  if (wire.id != null) inst.id = wire.id;
  if (wire.remaining_cents != null) {
    inst.remainingCents = safeCents(wire.remaining_cents);
  }
  if (wire.percentage != null) inst.percentage = wire.percentage;
  if (typeof wire.collectible === 'boolean')
    inst.collectible = wire.collectible;
  return inst;
}

/**
 * 1-based position of the current draw within the installment list, for the
 * "Installment N of M" caption. Matched to `current_draw` by label (the lists
 * share labels), falling back to the row flagged `current_draw`. Undefined when
 * neither resolves (the caption degrades to the draw label alone).
 */
function currentDrawPosition(
  installments: NonNullable<ProgressBilling['installments']>,
  currentLabel: string | null | undefined,
): number | undefined {
  let idx = currentLabel
    ? installments.findIndex((i) => i.label === currentLabel)
    : -1;
  if (idx < 0) {
    idx = installments.findIndex((i) => i.status === 'current_draw');
  }
  return idx >= 0 ? idx + 1 : undefined;
}

/**
 * Map the installment list and derive the "Installment N of M" caption context
 * (M = count, N = current draw position). Extracted to keep
 * `attachProgressBillingExtras` under the cyclomatic-complexity cap.
 */
function attachInstallments(
  out: ProgressBilling,
  spb: InvoiceDetailWireProgressBilling,
): void {
  if (!Array.isArray(spb.installments)) return;
  const installments = spb.installments.map(adaptInstallment);
  out.installments = installments;
  out.installmentCount = installments.length;
  const pos = currentDrawPosition(installments, spb.current_draw?.label);
  if (pos != null) out.installmentPosition = pos;
}

/** Attach the additive cents/flag fields the detail SPB block carries. */
function attachProgressBillingExtras(
  out: ProgressBilling,
  spb: InvoiceDetailWireProgressBilling,
): void {
  if (typeof spb.cancelled === 'boolean') out.cancelled = spb.cancelled;
  if (spb.past_due_amount_cents != null) {
    out.pastDueAmountCents = safeCents(spb.past_due_amount_cents);
  }
  if (spb.paid_to_date_cents != null) {
    out.paidToDateCents = safeCents(spb.paid_to_date_cents);
  }
  if (spb.total_balance_remaining_cents != null) {
    out.totalBalanceRemainingCents = safeCents(
      spb.total_balance_remaining_cents,
    );
  }

  const currentDraw = buildCurrentDraw(spb.current_draw);
  if (currentDraw) out.currentDraw = currentDraw;

  if (spb.next_draw_after_current) {
    out.nextDrawAfterCurrent = {
      label: str(spb.next_draw_after_current.label),
      dueDate: spb.next_draw_after_current.due_date ?? '',
    };
  }

  attachInstallments(out, spb);
}

/**
 * True when the nested SPB block should be dropped → regular (non-SPB) invoice.
 * Covers all three drop cases in one gate:
 *   - non-SPB (progress_billing null / absent / is_progress_billing false)
 *   - cancelled plan (D-03 — render as a regular invoice)
 *   - out-of-taxonomy payment_status (WR-01 → non-SPB fallthrough)
 */
function isNonSpb(
  spb: InvoiceDetailWireProgressBilling | null | undefined,
): spb is null | undefined {
  return (
    !spb ||
    !spb.is_progress_billing ||
    spb.cancelled === true ||
    !isKnownPaymentStatus(spb.payment_status)
  );
}

/**
 * Build the camelCase ProgressBilling block from the nested wire, or undefined
 * when the row is non-SPB (see `isNonSpb`).
 */
function buildProgressBilling(
  wire: InvoiceDetailWire,
): ProgressBilling | undefined {
  const spb = wire.progress_billing;
  // IN-04: `isNonSpb` already returns true for an out-of-taxonomy
  // payment_status (see its definition), so it is the single gate here. The
  // predicate narrows `spb` to non-null, keeping the `spb.payment_status` read
  // below well-typed.
  if (isNonSpb(spb)) {
    return undefined;
  }

  // `isNonSpb(spb)` returning false guarantees `isKnownPaymentStatus(
  // spb.payment_status)` (it is one of the gate's drop clauses), so the code
  // is narrowed to the closed union here. The assertion documents that
  // invariant without re-running the predicate (IN-04).
  const out: ProgressBilling = {
    paymentStatus: spb.payment_status as ProgressBilling['paymentStatus'],
    contractTotalCents: safeCents(spb.contract_total_cents),
  };
  attachProgressBillingExtras(out, spb);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main adapter
// ─────────────────────────────────────────────────────────────────────────────

/** Resolve the PDF source: prefer links.invoice_pdf, fall back to pdf_url (D-16). */
function resolvePdfUrl(wire: InvoiceDetailWire): string | undefined {
  const fromLinks = wire.links?.invoice_pdf?.href;
  if (fromLinks != null) return fromLinks;
  if (wire.pdf_url != null) return wire.pdf_url;
  return undefined;
}

/**
 * Extract the server's authoritative `next_payment_key` from `payment_create_url`
 * (CR-01). It is the SOLE source of the idempotency key — the client must NOT mint
 * its own (a UUID defeats the Sera unique-index + GPI `reference_id` + Stax
 * `idempotency_id` dedup, all keyed on this value). Tolerates a relative URL via a
 * throwaway base; `URLSearchParams` decodes the percent-encoded colons so the
 * `id:total:balance[:failed]` key round-trips verbatim on the charge POST.
 */
function resolveNextPaymentKey(wire: InvoiceDetailWire): string | undefined {
  const url = wire.payment_create_url;
  if (typeof url !== 'string' || url.length === 0) return undefined;
  try {
    const key = new URL(url, 'https://placeholder.invalid').searchParams.get(
      'next_payment_key',
    );
    return key != null && key.length > 0 ? key : undefined;
  } catch {
    return undefined;
  }
}

/** Map the always-present base totals + collection fields. */
function buildInvoiceBase(wire: InvoiceDetailWire): InvoiceDetail {
  const financials = wire.financials ?? {};
  return {
    id: normalizeId(wire.invoice_id),
    cancelled: wire.cancelled === true,
    voided: wire.voided === true,
    finalized: wire.finalized === true,
    lineItems: (wire.items_attributes ?? []).map(adaptLineItemNode),
    subtotalCents: safeCents(financials.subtotal_cents),
    discountCents: safeCents(financials.discount_amount_cents),
    lateFeeCents: safeCents(financials.late_fee_cents),
    salesTaxCents: safeCents(financials.sales_tax_cents),
    totalCents: safeCents(financials.total_cents),
    totalTransactionsCents: safeCents(financials.total_transactions_cents),
    balanceCents: safeCents(financials.balance_cents),
    payments: (wire.payments ?? []).map(adaptTransaction),
    notes: (wire.notes ?? []).map(adaptNote),
  };
}

/** Attach the optional scalar fields the wire only sometimes emits. */
function attachOptionalScalars(
  detail: InvoiceDetail,
  wire: InvoiceDetailWire,
): void {
  if (wire.job_id != null) detail.jobId = normalizeId(wire.job_id);
  if (wire.invoice_date != null) detail.invoiceDate = wire.invoice_date;
  if (wire.format != null) detail.displayFormat = wire.format;
  if (typeof wire.display_membership_savings === 'boolean') {
    detail.displayMembershipSavings = wire.display_membership_savings;
  }

  // savings_cents: omit when absent, preserve when present (including 0) — D-12.
  const savings = wire.financials?.savings_cents;
  if (savings !== undefined && savings !== null) {
    detail.savingsCents = safeCents(savings);
  }

  const pdfUrl = resolvePdfUrl(wire);
  if (pdfUrl != null) detail.pdfUrl = pdfUrl;
  if (wire.links) detail.links = wire.links;
}

/** Attach the optional nested relationships (customer, addresses, signature, SPB). */
function attachOptionalRelations(
  detail: InvoiceDetail,
  wire: InvoiceDetailWire,
): void {
  const customer = adaptCustomer(wire.customer);
  if (customer) detail.customer = customer;
  if (wire.billing_address) {
    detail.billingAddress = adaptAddress(wire.billing_address);
  }
  if (wire.service_address) {
    detail.serviceAddress = adaptAddress(wire.service_address);
  }

  const signature = adaptSignature(wire.signature);
  if (signature) detail.signature = signature;

  const progressBilling = buildProgressBilling(wire);
  if (progressBilling) detail.progressBilling = progressBilling;
}

/**
 * Pure adapter: maps the `GET /v2/invoices/:id` wire to the typed
 * `InvoiceDetail` domain shape. All wire-contract drift is absorbed here.
 */
export function adaptInvoiceDetail(wire: InvoiceDetailWire): InvoiceDetail {
  const detail = buildInvoiceBase(wire);
  attachOptionalScalars(detail, wire);
  attachOptionalRelations(detail, wire);
  // CR-01: the server idempotency key (parsed from payment_create_url). Attached
  // here rather than in attachOptionalScalars to keep that helper under the
  // cyclomatic-complexity ceiling.
  const nextPaymentKey = resolveNextPaymentKey(wire);
  if (nextPaymentKey != null) detail.nextPaymentKey = nextPaymentKey;
  return detail;
}
